import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiCall } from '../../utils/fetchAPI';
import { translateApiMessage } from '../../utils/apiMessages';
import { useCityInView } from '../city/useHubCity';

const DEFAULT_PAGE_SIZE = 12;

/**
 * Flatten the category tree into the options a select needs, keeping the
 * parent as a group label.
 */
export function toCategoryOptions(tree) {
  return tree.flatMap((root) => [
    { slug: root.slug, label: root.name, group: null, parentSlug: null, isRoot: true },
    ...(root.children || []).map((child) => ({
      slug: child.slug,
      label: child.name,
      group: root.name,
      // The family a trade belongs to, by slug: the filter is a slug, so
      // working out which family is open means comparing slugs, not names.
      parentSlug: root.slug,
      isRoot: false,
    })),
  ]);
}

/**
 * Directory search state, kept in the URL.
 *
 * The URL is the single source of truth for the filters, so a resident can
 * share or bookmark a search and land on the same results.
 */
export function useDirectory({ categorySlug: routeCategorySlug, kind = null } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { city: cityInView, cityParam } = useCityInView();

  const filters = useMemo(
    () => ({
      // A category in the path wins over one in the query string.
      category: routeCategorySlug || searchParams.get('category') || '',
      neighborhood: searchParams.get('neighborhood') || '',
      q: searchParams.get('q') || '',
      page: Number(searchParams.get('page')) || 1,
    }),
    [routeCategorySlug, searchParams]
  );

  const [categories, setCategories] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [providers, setProviders] = useState([]);
  const [meta, setMeta] = useState(null);
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Categories and neighborhoods do not change with the filters, so they are
  // fetched once.
  useEffect(() => {
    let cancelled = false;

    const loadReferenceData = async () => {
      try {
        // Each page asks for its own tree: the shops' menu is "Boulangerie",
        // "Primeur", and the services' is "Plomberie" — one list holding both
        // is a list nobody reads to the end.
        const categoriesResponse = await apiCall(
          kind ? `/categories?kind=${encodeURIComponent(kind)}` : '/categories',
          { method: 'GET' }
        );

        if (!categoriesResponse.ok) {
          throw new Error('Les catégories n’ont pas pu être chargées.');
        }

        const categoriesData = await categoriesResponse.json();

        if (cancelled) return;

        setCategories(categoriesData.data || []);

        // The neighborhood filter needs to know which city is open.
        if (cancelled) return;

        setCity(cityInView);

        if (cityInView) {
          const neighborhoodsResponse = await apiCall(`/cities/${cityInView.slug}/neighborhoods`, {
            method: 'GET',
          });

          if (neighborhoodsResponse.ok) {
            const neighborhoodsData = await neighborhoodsResponse.json();
            if (!cancelled) {
              setNeighborhoods(neighborhoodsData.data || []);
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      }
    };

    loadReferenceData();

    return () => {
      cancelled = true;
    };
  }, [kind, cityInView]);

  useEffect(() => {
    let cancelled = false;

    const loadProviders = async () => {
      setLoading(true);

      const params = new URLSearchParams();
      if (filters.category) params.set('category', filters.category);
      if (filters.neighborhood) params.set('neighborhood', filters.neighborhood);
      if (filters.q) params.set('q', filters.q);
      // Fixed by the page rather than chosen by the reader: services and shops
      // are two pages now, not two values of a filter.
      if (kind) params.set('kind', kind);
      // A visitor says which city they are looking at; a resident's account
      // already says it.
      if (cityParam) params.set('city', cityParam);
      params.set('page', String(filters.page));
      params.set('limit', String(DEFAULT_PAGE_SIZE));

      try {
        const response = await apiCall(`/providers?${params.toString()}`, { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (cancelled) return;

        if (!response.ok) {
          throw new Error(translateApiMessage(data.error) || 'L’annuaire n’a pas pu être chargé.');
        }

        setProviders(data.data || []);
        setMeta(data.meta || null);
        setError('');
      } catch (err) {
        if (!cancelled) {
          setProviders([]);
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProviders();

    return () => {
      cancelled = true;
    };
  }, [filters, kind, cityParam]);

  /**
   * Changing any filter resets to the first page: staying on page 4 of a
   * narrower search would show an empty list.
   */
  const setFilter = useCallback(
    (name, value) => {
      const next = new URLSearchParams(searchParams);

      if (value) {
        next.set(name, value);
      } else {
        next.delete(name);
      }

      if ('page' !== name) {
        next.delete('page');
      }

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const resetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const categoryOptions = useMemo(() => toCategoryOptions(categories), [categories]);

  return {
    filters,
    categories,
    categoryOptions,
    neighborhoods,
    providers,
    meta,
    city,
    loading,
    error,
    setFilter,
    resetFilters,
    pageSize: DEFAULT_PAGE_SIZE,
  };
}
